import {VcmpClient} from "@variocube/vcmp";
import {useEffect, useMemo} from "react";
import {getBaseUrl} from "./client";
import {z} from "zod";

export enum LockStatus {
	Open = 'Open',
	Closed = 'Closed',
	Breakin = 'Breakin',
	Blocked = 'Blocked',
}

export type Occupancy = {
	uuid: string,
	cubeDescription: string,
	cubeId: string,
	boxNumber: string,
	lockStatus: LockStatus,
	occupiedFrom: string,
	openedTimes: number;
}

const WebSocketBaseUrl = getBaseUrl().replace('http', 'ws');

export class BookingVcmpClient extends VcmpClient {

	constructor(public readonly uuid: string) {
		super(WebSocketBaseUrl + `/api/booking/v2/vcmp/${uuid}`);
	}
}

export function useBookingClient(uuid: string) {

	const client = useMemo(() => {
		return new BookingVcmpClient(uuid);
	}, [uuid]);

	useEffect(() => {
		client.start();
		return () => client.stop()
	}, [client]);

	return client;
}

export enum BookingEventType {
	B2OccupancyEvent = "booking:B2Occupancy",
	B2ConfirmationEvent = "booking:B2Confirmation"
}

type BookingEvents = {
	[BookingEventType.B2OccupancyEvent]: Occupancy,
	[BookingEventType.B2ConfirmationEvent]: { bookingId: string, paymentId: string|null, paidAt: string|null },
}

type EventHandler<EventType extends keyof BookingEvents> = (event: BookingEvents[EventType]) => any;

export function useBookingEvent<EventType extends keyof BookingEvents>(client: BookingVcmpClient, type: EventType, handler: EventHandler<EventType>) {

	useEffect(() => {
		client.on(type, handler);
		return () => client.off(type);
	}, [client, type, handler]);
}

export type BookingBoxes = z.infer<typeof BookingBoxes>;
const BookingBoxes = z.record(z.string(), z.number());
